import type { ServerMessage, WrappedCoin } from "@screener/protocol";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import {
  type AlertRulesMap,
  actionsOf,
  alertKey,
  needsNotify,
  needsSound,
  notificationText,
  rulesMap,
} from "./alert-actions";
import {
  type AlertNotificationPermission,
  type AlertNotify,
  createAlertNotify,
} from "./alert-notify";
import { type AlertRulesListResult, fetchAlertRules } from "./alert-rules-api";
import { type AlertSound, createAlertSound } from "./alert-sound";
import { ALERT_DEDUP_WINDOW_MS, claim } from "./tab-claim";

/**
 * Логика §8 (1.20) вынесена из React тем же приёмом, что у `use-favorites.ts`
 * (`sendFavoriteToggle`/`guardedFavoriteToggle`): контроллер — обычные
 * функции с внедрёнными зависимостями, тестируемые без рендера. Хук
 * `useAlertActions` ниже — тонкая обёртка, которая один раз на монтирование
 * заводит реальные обёртки браузерных API и держит контроллер в `useRef`.
 *
 * Снимок для шапки идёт через `useSyncExternalStore(controller.subscribe,
 * controller.getSnapshot)` (ревью stage6-alert-sound, пункт 3): контроллер —
 * внешнее хранилище, а не состояние React, и `getSnapshot()` кеширует объект
 * снимка между изменениями, чтобы React не перерисовывал шапку впустую.
 */

/** Флаги шапки §9 (1.20) — снимок состояния звука, разрешения и правил. */
export interface AlertHeaderState {
  soundBlocked: boolean;
  permission: AlertNotificationPermission;
  hasSoundRules: boolean;
  hasNotifyRules: boolean;
}

export interface AlertControllerDeps {
  /** Чтение правил §7.6 — `fetchAlertRules` в проде, подмена в тестах. */
  fetchRules: () => Promise<AlertRulesListResult>;
  sound: AlertSound;
  notify: AlertNotify;
  /** Заявка на дедупликацию между вкладками — `claim` из `tab-claim.ts`. */
  claim: (key: string, holdMs: number) => Promise<boolean>;
  onSessionInvalid: () => void;
}

export interface AlertController {
  /** Заводит звук (`sound.start()`), подписки на звук/разрешение и читает правила один раз (design.md решение 1). */
  start(): void;
  /** Сбрасывает карту в пустую, освобождает звук (`sound.dispose()`) и снимает подписки; ответы чтений, начатых до вызова, игнорируются. */
  stop(): void;
  /** Перечитывает правила; чтение, уже идущее, отдаёт всем ждущим один и тот же результат. */
  reload(): Promise<void>;
  /**
   * Перечитывает правила свежим чтением после записи (ревью stage6-alert-web,
   * пункт A.4): не подцепляется к чтению, начатому ДО записи — дожидается
   * его и запускает новое. `reload()` с присоединением остаётся как есть —
   * им пользуются фокус окна и «незнакомое правило» в `handle()`.
   */
  refresh(): Promise<void>;
  /**
   * Обрабатывает сообщение потока §7.6 — не `alert_fired` игнорируется.
   * `wrapped` — перечень обёрнутых монет из приветствия: заголовок
   * нотификации называет инструмент и несёт пометку. `exchangeBases` —
   * индекс баз в написании биржи (`StreamState.exchange_bases`), чтобы
   * нотификация писала инструмент тикером (§63).
   */
  handle(
    message: ServerMessage,
    wrapped?: readonly WrappedCoin[],
    exchangeBases?: ReadonlyMap<string, string>,
  ): Promise<void>;
  /** Подписка на изменение снимка шапки — для `useSyncExternalStore`. */
  subscribe(listener: () => void): () => void;
  /** Снимок для шапки §9. Тот же объект, пока ничего не менялось. */
  getSnapshot(): AlertHeaderState;
  /** Запрашивает разрешение браузера — только по жесту пользователя (design.md решение 3). */
  requestPermission(): Promise<void>;
  /** Снимает блокировку звука тем же путём, что и жест (клик по пометке в шапке). */
  unlockSound(): void;
}

export function createAlertController(deps: AlertControllerDeps): AlertController {
  let map: AlertRulesMap = new Map();
  // Поколение растёт на каждый `stop()`: ответ чтения, начатого до него,
  // сверяет своё поколение с текущим и молча отбрасывается, если оно
  // устарело — тот же приём, что `createFavoritesReloader`, но по признаку
  // остановки, а не по порядковому номеру каждого вызова.
  let generation = 0;
  let inFlight: Promise<void> | null = null;

  const listeners = new Set<() => void>();
  let cachedSnapshot: AlertHeaderState | undefined;
  let snapshotDirty = true;

  function invalidate(): void {
    snapshotDirty = true;
    for (const listener of listeners) {
      listener();
    }
  }

  function getSnapshot(): AlertHeaderState {
    if (snapshotDirty || !cachedSnapshot) {
      cachedSnapshot = {
        soundBlocked: deps.sound.isBlocked(),
        permission: deps.notify.permission(),
        hasSoundRules: needsSound(map),
        hasNotifyRules: needsNotify(map),
      };
      snapshotDirty = false;
    }
    return cachedSnapshot;
  }

  function doReload(): Promise<void> {
    if (inFlight) {
      return inFlight;
    }
    const myGeneration = generation;
    const promise: Promise<void> = deps
      .fetchRules()
      .then((result) => {
        if (myGeneration !== generation) {
          // `stop()` состоялся, пока ответ был в пути — не наш больше.
          return;
        }
        if (result.status === "ok") {
          map = rulesMap(result.rules);
          invalidate();
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        // `unavailable` — прежняя карта остаётся (design.md решение 1):
        // лучше сыграть по вчерашним флагам, чем промолчать.
      })
      .finally(() => {
        // Снимаем себя, только если мы всё ещё текущее чтение: `stop()`
        // мог уже обнулить `inFlight`, и более новое чтение — не наше дело.
        if (inFlight === promise) {
          inFlight = null;
        }
      });
    inFlight = promise;
    return promise;
  }

  // Подписки на звук и разрешение живут между `start()` и `stop()`, не между
  // созданием контроллера и `dispose()`: контроллер держится в `useRef`
  // хука и не пересоздаётся, а в React StrictMode эффекты монтируются,
  // снимаются и монтируются снова — подписка, заведённая один раз при
  // создании и снятая по «отписаться навсегда», такого повторного монтажа
  // не переживает (ревью stage6-alert-sound, пункт про дублирующий
  // followup). `start()`/`stop()` идемпотентны: повторный `start()` не
  // подписывается дважды, `stop()` без предшествующего `start()` не падает.
  let subscribed = false;
  let unsubscribeSound: (() => void) | undefined;
  let unsubscribeNotify: (() => void) | undefined;

  function start(): void {
    deps.sound.start();
    if (!subscribed) {
      subscribed = true;
      unsubscribeSound = deps.sound.subscribe(() => invalidate());
      unsubscribeNotify = deps.notify.subscribe(() => invalidate());
    }
    void doReload();
  }

  function stop(): void {
    generation += 1;
    inFlight = null;
    map = new Map();
    deps.sound.dispose();
    if (subscribed) {
      subscribed = false;
      unsubscribeSound?.();
      unsubscribeNotify?.();
      unsubscribeSound = undefined;
      unsubscribeNotify = undefined;
    }
    invalidate();
  }

  function reload(): Promise<void> {
    return doReload();
  }

  async function refresh(): Promise<void> {
    const myGeneration = generation;
    if (inFlight) {
      await inFlight;
    }
    if (myGeneration !== generation) {
      // `stop()` случился, пока мы ждали идущее чтение — новое чтение не
      // запускаем: вкладка, попросившая `refresh()`, его больше не ждёт
      // (ревью, второй круг, пункт 6).
      return;
    }
    await doReload();
  }

  async function handle(
    message: ServerMessage,
    wrapped: readonly WrappedCoin[] = [],
    exchangeBases: ReadonlyMap<string, string> = new Map(),
  ): Promise<void> {
    if (message.type !== "alert_fired") {
      return;
    }
    let actions = actionsOf(map, message);
    if (actions === "unknown") {
      // Если это чтение уже идёт по другой причине (например, `start()`),
      // оно могло начаться раньше, чем правило было заведено, и потому не
      // знать о нём. Присоединение к нему заслуживает ровно одно
      // собственное дочитывание, не больше (спека `web-alert-sound»,
      // «Правило заведено, пока чтение уже в пути»).
      const joinedExistingRead = inFlight !== null;
      await doReload();
      actions = actionsOf(map, message);
      if (actions === "unknown" && joinedExistingRead) {
        await doReload();
        actions = actionsOf(map, message);
      }
      if (actions === "unknown") {
        // Правило удалено — ни звука, ни нотификации (спека `web-alert-sound`,
        // «Правило удалено до срабатывания»).
        return;
      }
    }

    // Звук и нотификация согласуются раздельными заявками (ревью
    // stage6-alert-sound, пункт 1, design.md решение 4): вкладка без звука
    // (заблокирован или выключен) не должна забирать блокировку у соседней
    // вкладки, которая могла бы его проиграть, — каждое действие просит
    // заявку только на то, что может выполнить сама.
    const wantSound = actions.sound && !deps.sound.isBlocked();
    const wantNotify = actions.notify && deps.notify.permission() === "granted";
    if (!wantSound && !wantNotify) {
      return;
    }

    const key = alertKey(message);

    if (wantSound) {
      const claimed = await deps.claim(`sound|${key}`, ALERT_DEDUP_WINDOW_MS);
      if (claimed) {
        deps.sound.play();
      }
    }

    if (wantNotify) {
      const claimed = await deps.claim(`notify|${key}`, ALERT_DEDUP_WINDOW_MS);
      if (claimed) {
        const { title, body } = notificationText(message, wrapped, exchangeBases);
        deps.notify.show(title, body, key);
      }
    }
  }

  async function requestPermission(): Promise<void> {
    await deps.notify.request();
    invalidate();
  }

  function unlockSound(): void {
    deps.sound.unlock();
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    start,
    stop,
    reload,
    refresh,
    handle,
    subscribe,
    getSnapshot,
    requestPermission,
    unlockSound,
  };
}

/** Минимальная форма цели подписки на `focus` — по умолчанию `window`. */
export interface FocusTarget {
  addEventListener(type: "focus", listener: () => void): void;
  removeEventListener(type: "focus", listener: () => void): void;
}

/**
 * Подписка на `focus` окна вынесена отдельной функцией ради теста без
 * React и без реального `window` (tasks.md 4.1): правка правила в другой
 * вкладке доходит до этой по возвращению фокуса (design.md решение 1).
 */
export function subscribeWindowFocus(target: FocusTarget, callback: () => void): () => void {
  target.addEventListener("focus", callback);
  return () => target.removeEventListener("focus", callback);
}

interface UseAlertActionsOptions {
  /** Поток и правила держим, только пока в клиенте есть действительная сессия. */
  enabled: boolean;
  onSessionInvalid: () => void;
}

export interface UseAlertActionsResult {
  /** Пропустить сообщение потока §7.6 через контроллер — вызывающая сторона не ждёт. */
  onMessage: (
    message: ServerMessage,
    wrapped: readonly WrappedCoin[],
    exchangeBases: ReadonlyMap<string, string>,
  ) => void;
  soundBlocked: boolean;
  permission: AlertNotificationPermission;
  hasSoundRules: boolean;
  hasNotifyRules: boolean;
  requestPermission: () => void;
  unlockSound: () => void;
  /**
   * Перечитывает карту действий вкладки свежим чтением, не дожидаясь фокуса
   * окна (design.md stage6-alert-web решение 7, спека `web-alert-sound`
   * «Звук выключен на экране алертов этой вкладки»): экран «Алерты» этой же
   * вкладки зовёт это после каждой успешной записи правила. Свежесть —
   * ревью stage6-alert-web, пункт A.4: не подцепляется к чтению, начатому
   * до записи. Та же ссылка на каждый рендер — `controller.refresh` берётся
   * напрямую с уже созданного контроллера.
   */
  refresh: () => Promise<void>;
}

/**
 * Звук и нотификация по срабатыванию правила §8 (1.20) — хук над
 * `createAlertController`, тем же приёмом, что `useFavorites` над
 * `sendFavoriteToggle`: реальные обёртки браузерных API заводятся один раз
 * на монтирование (без побочных эффектов при самом создании — они у
 * `createAlertSound`/`createAlertNotify` начинаются только со `start()`), а
 * вся логика решения — в чистом контроллере.
 */
export function useAlertActions({
  enabled,
  onSessionInvalid,
}: UseAlertActionsOptions): UseAlertActionsResult {
  const onSessionInvalidRef = useRef(onSessionInvalid);
  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);

  const controllerRef = useRef<AlertController | undefined>(undefined);
  if (!controllerRef.current) {
    controllerRef.current = createAlertController({
      fetchRules: fetchAlertRules,
      sound: createAlertSound(),
      notify: createAlertNotify(),
      claim,
      onSessionInvalid: () => onSessionInvalidRef.current(),
    });
  }
  const controller = controllerRef.current;

  useEffect(() => {
    if (enabled) {
      controller.start();
      return () => controller.stop();
    }
    controller.stop();
    return undefined;
  }, [enabled, controller]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return subscribeWindowFocus(window, () => {
      void controller.reload();
    });
  }, [enabled, controller]);

  const onMessage = useCallback(
    (
      message: ServerMessage,
      wrapped: readonly WrappedCoin[],
      exchangeBases: ReadonlyMap<string, string>,
    ) => {
      // Страховка от необработанного отказа (ревью stage6-alert-sound,
      // пункт 5): `handle()` сам не должен бросать, но обработчик потока —
      // не место, где такой отказ дозволено потерять молча.
      controller.handle(message, wrapped, exchangeBases).catch((error: unknown) => {
        console.error("не удалось обработать срабатывание алерта", error);
      });
    },
    [controller],
  );

  const requestPermission = useCallback(() => {
    void controller.requestPermission();
  }, [controller]);

  const unlockSound = useCallback(() => {
    controller.unlockSound();
  }, [controller]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  return {
    onMessage,
    soundBlocked: snapshot.soundBlocked,
    permission: snapshot.permission,
    hasSoundRules: snapshot.hasSoundRules,
    hasNotifyRules: snapshot.hasNotifyRules,
    requestPermission,
    unlockSound,
    refresh: controller.refresh,
  };
}
